<script setup lang="ts">
import {
  Database as DatabaseIcon,
  Download as DownloadIcon,
  Loader2 as LoaderIcon,
  Map as MapIcon,
  Pause as PauseIcon,
  Play as PlayIcon,
  Trash2 as TrashIcon,
  WifiOff as WifiOffIcon,
} from '@lucide/vue'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import MapHeader from '@/components/MapHeader.vue'
import RoutePageHeader from '@/components/RoutePageHeader.vue'
import BaseBadge from '@/components/ui/BaseBadge.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseEmptyState from '@/components/ui/BaseEmptyState.vue'
import BaseIconButton from '@/components/ui/BaseIconButton.vue'
import BaseProgressBar from '@/components/ui/BaseProgressBar.vue'
import { connectivityService } from '@/services/connectivityService'
import { showConfirm } from '@/services/dialogService'
import { formatByteSize } from '@/services/formattingService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { MANUAL_MAP_DISPLAY_NAME, MANUAL_MAP_PACKAGE_ID } from '@/services/offlineTilesService'
import { isTauri } from '@/services/platformService'
import { HILLSHADE_SOURCE_ID } from '@/services/routecampContours'
import { requestRouteModeLeave } from '@/services/routeModeLeaveGuardService'
import { useSafeAreaHeaderClearance } from '@/services/safeAreaService'
import {
  useOfflineStore,
  type ActiveDownload,
  type OfflineEntry,
  type OfflineStatus,
} from '@/stores/offlineStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

const router = useRouter()
const route = useRoute()
const offlineStore = useOfflineStore()
const workspaceStore = useWorkspaceStore()
const pageTopPadding = useSafeAreaHeaderClearance(48)
const clearingTileCache = ref(false)
const isRefreshingOfflineData = ref(false)
const refreshError = ref<string | null>(null)
const canResumeDownloads = connectivityService.isEffectivelyOnline
let refreshPromise: Promise<void> | null = null
const manualMapDownloadBusy = computed(
  () =>
    Boolean(offlineStore.active[MANUAL_MAP_PACKAGE_ID]) ||
    Boolean(offlineStore.pending[MANUAL_MAP_PACKAGE_ID]),
)
const hasManualMapPackage = computed(
  () => Boolean(offlineStore.index[MANUAL_MAP_PACKAGE_ID]) || manualMapDownloadBusy.value,
)

interface OfflineListEntry extends OfflineEntry {
  activeDownload: ActiveDownload | null
  progressPercent: number | null
  isStored: boolean
  pendingError: string | null
  isPaused: boolean
}

function refreshOfflineDataView(): Promise<void> {
  if (!isTauri()) return Promise.resolve()
  if (refreshPromise) return refreshPromise

  isRefreshingOfflineData.value = true
  refreshError.value = null
  const operation = offlineStore
    .refreshOverview()
    .catch((error) => {
      refreshError.value = error instanceof Error ? error.message : 'Could not refresh offline data'
    })
    .finally(() => {
      if (refreshPromise !== operation) return
      refreshPromise = null
      isRefreshingOfflineData.value = false
    })
  refreshPromise = operation
  return operation
}

watch(
  () => route.name,
  (name) => {
    if (name === 'offline-data') {
      void refreshOfflineDataView()
    }
  },
  { immediate: true },
)

const entries = computed<OfflineListEntry[]>(() => {
  const merged = new Map<string, OfflineListEntry>()

  for (const entry of Object.values(offlineStore.index)) {
    merged.set(entry.workspaceId, {
      ...entry,
      activeDownload: null,
      progressPercent: null,
      isStored: true,
      pendingError: null,
      isPaused: false,
    })
  }

  for (const pendingDownload of Object.values(offlineStore.pending)) {
    const existing = merged.get(pendingDownload.workspaceId)

    merged.set(pendingDownload.workspaceId, {
      workspaceId: pendingDownload.workspaceId,
      packageKind: existing?.packageKind ?? pendingDownload.input.package_kind ?? 'workspace',
      workspaceName: existing?.workspaceName ?? pendingDownload.workspaceName,
      coverageCells: existing?.coverageCells ?? pendingDownload.input.coverage_cells ?? [],
      includeTerrain: existing?.includeTerrain ?? pendingDownload.input.include_terrain ?? false,
      sourceIds: existing?.sourceIds ?? [],
      sourceExtensions: existing?.sourceExtensions ?? {},
      sourceMinzooms: existing?.sourceMinzooms ?? {},
      sourceMaxzooms: existing?.sourceMaxzooms ?? {},
      status: pendingDownload.lastError
        ? 'error'
        : pendingDownload.paused
          ? 'partial'
          : 'downloading',
      downloadedAt: existing?.downloadedAt ?? 0,
      sizeBytes: existing?.sizeBytes ?? 0,
      tileCount: existing?.tileCount ?? 0,
      photoCount: existing?.photoCount ?? pendingDownload.photoCount,
      photosIncluded: existing?.photosIncluded ?? pendingDownload.photosIncluded,
      styleId: existing?.styleId ?? pendingDownload.styleId,
      workspaceHash: existing?.workspaceHash ?? pendingDownload.input.workspace_hash,
      activeDownload: existing?.activeDownload ?? null,
      progressPercent: existing?.progressPercent ?? null,
      isStored: Boolean(existing),
      pendingError: pendingDownload.lastError,
      isPaused: pendingDownload.paused,
    })
  }

  for (const [workspaceId, download] of Object.entries(offlineStore.active)) {
    const existing = merged.get(workspaceId)
    const progressPercent =
      download.overallTotal === 0
        ? 0
        : Math.round((download.overallDone / download.overallTotal) * 100)

    merged.set(workspaceId, {
      workspaceId,
      packageKind: existing?.packageKind ?? 'workspace',
      workspaceName: existing?.workspaceName ?? download.workspaceName ?? null,
      coverageCells: existing?.coverageCells ?? [],
      includeTerrain: existing?.includeTerrain ?? false,
      sourceIds: existing?.sourceIds ?? [],
      sourceExtensions: existing?.sourceExtensions ?? {},
      sourceMinzooms: existing?.sourceMinzooms ?? {},
      sourceMaxzooms: existing?.sourceMaxzooms ?? {},
      status: download.error ? 'error' : 'downloading',
      downloadedAt: existing?.downloadedAt ?? 0,
      sizeBytes: existing?.sizeBytes ?? 0,
      tileCount: existing?.tileCount ?? 0,
      photoCount: existing?.photoCount ?? download.photoCount ?? 0,
      photosIncluded: existing?.photosIncluded ?? download.photosIncluded ?? false,
      styleId: existing?.styleId ?? download.styleId ?? '',
      workspaceHash: existing?.workspaceHash ?? '',
      activeDownload: download,
      progressPercent,
      isStored: Boolean(existing),
      pendingError: existing?.pendingError ?? download.error ?? null,
      isPaused: false,
    })
  }

  return Array.from(merged.values()).sort((a, b) => {
    const activeDiff = Number(Boolean(b.activeDownload)) - Number(Boolean(a.activeDownload))
    if (activeDiff !== 0) return activeDiff
    return b.downloadedAt - a.downloadedAt
  })
})

function formatDate(secs: number): string {
  return new Date(secs * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'
function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'downloading':
      return 'info'
    case 'ready':
      return 'success'
    case 'partial':
      return 'warning'
    case 'error':
      return 'danger'
    default:
      return 'neutral'
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'downloading':
      return 'Downloading'
    case 'ready':
      return 'Ready'
    case 'partial':
      return 'Partial'
    case 'error':
      return 'Error'
    default:
      return status
  }
}

function activeDownloadStatusLabel(download: ActiveDownload): string {
  if (download.error) return download.error
  if (download.phase === 'waiting') return 'Waiting for another download to finish…'
  if (download.phase === 'meta') return 'Preparing offline package'
  if (download.phase === 'tiles') return 'Downloading map tiles'
  return 'Downloading photos'
}

function entryStatusMessage(entry: OfflineListEntry): string {
  if (entry.activeDownload) return activeDownloadStatusLabel(entry.activeDownload)
  if (entry.pendingError) return entry.pendingError
  if (entry.isPaused) return 'Download paused'
  return ''
}

function entryStatusMessageClass(entry: OfflineListEntry): string {
  if (entry.pendingError || entry.activeDownload?.error) {
    return 'border-red-100 bg-red-50 text-red-700'
  }

  if (entry.isPaused) {
    return 'border-amber-100 bg-amber-50 text-amber-700'
  }

  return 'border-gray-200 bg-gray-50 text-gray-600'
}

function entryInlineStatusClass(entry: OfflineListEntry): string {
  if (entry.pendingError || entry.activeDownload?.error) return 'text-red-700'
  if (entry.isPaused) return 'text-amber-700'
  return 'text-gray-600'
}

function downloadedLabel(entry: OfflineListEntry): string {
  if (entry.activeDownload) {
    return entry.activeDownload.error ? 'Not completed' : 'In progress'
  }

  if (!entry.downloadedAt) return '-'
  return formatDate(entry.downloadedAt)
}

function sizeLabel(entry: OfflineListEntry): string {
  if (!entry.isStored && !entry.sizeBytes) return '-'
  return formatByteSize(entry.sizeBytes)
}

function tileCountLabel(entry: OfflineListEntry): string {
  if (!entry.isStored && entry.tileCount === 0) return '-'
  return entry.tileCount.toLocaleString()
}

function styleLabel(entry: OfflineListEntry): string {
  return entry.styleId || '-'
}

// null = unknown/not applicable (pending downloads have empty sourceIds; only
// the routecamp style has a terrain source).
function terrainLabel(entry: OfflineListEntry): string | null {
  if (entry.packageKind === 'map_area' && entry.includeTerrain) {
    return 'Includes contours and terrain shading'
  }
  if (!entry.isStored || entry.styleId !== 'routecamp' || entry.sourceIds.length === 0) return null
  return entry.sourceIds.includes(HILLSHADE_SOURCE_ID)
    ? 'Includes terrain'
    : 'Map data only (no terrain)'
}

function isDownloadInProgress(entry: OfflineListEntry): boolean {
  return Boolean(entry.activeDownload && !entry.activeDownload.error)
}

function canResume(entry: OfflineListEntry): boolean {
  return entry.isPaused || Boolean(entry.pendingError)
}

function canPause(entry: OfflineListEntry): boolean {
  return isDownloadInProgress(entry)
}

function rowStatus(entry: OfflineListEntry): OfflineStatus {
  return entry.status
}

function workspaceName(entry: { workspaceId: string; workspaceName: string | null }): string {
  if (entry.workspaceId === MANUAL_MAP_PACKAGE_ID) return MANUAL_MAP_DISPLAY_NAME
  if (entry.workspaceName && entry.workspaceName.trim().length > 0) return entry.workspaceName
  if (workspaceStore.clientId === entry.workspaceId && workspaceStore.name.trim().length > 0) {
    return workspaceStore.name
  }
  return entry.workspaceId.substring(0, 8) + '…'
}

async function handleNavigateToWorkspace(workspaceId: string) {
  if (workspaceId === MANUAL_MAP_PACKAGE_ID) return
  await router.push({ name: 'workspace-detail', params: { workspaceId } })
}

async function handleOpenMapAreaSelector() {
  if (
    offlineStore.ownerUserId === null ||
    !connectivityService.isEffectivelyOnline.value ||
    manualMapDownloadBusy.value
  ) {
    return
  }
  if (!(await requestRouteModeLeave())) return
  await router.replace({ name: 'offline-map-areas' })
}

async function handleDelete(workspaceId: string, name: string) {
  if (offlineStore.deleting[workspaceId]) return

  const confirmed = await showConfirm({
    title: 'Remove offline data?',
    message: `Delete locally stored map tiles and data for "${name}"? This cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (!confirmed) return

  try {
    await offlineStore.deleteOffline(workspaceId)
    await refreshOfflineDataView()
  } catch (error) {
    logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to delete offline workspace from UI', {
      workspaceId,
      error,
    })
  }
}

async function handleResume(workspaceId: string) {
  await offlineStore.resumeDownload(workspaceId)
}

async function handlePause(workspaceId: string) {
  await offlineStore.pauseDownload(workspaceId)
}

async function handleClearTileCache() {
  if (clearingTileCache.value) return

  try {
    clearingTileCache.value = true
    await offlineStore.clearTileCache()
  } catch (error) {
    logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to clear tile cache from UI', { error })
  } finally {
    clearingTileCache.value = false
  }
}
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col overflow-hidden bg-[#FEFEFE]">
    <MapHeader />

    <main class="min-h-0 flex-1 overflow-y-auto" :style="{ paddingTop: pageTopPadding }">
      <RoutePageHeader
        eyebrow="Storage"
        title="Offline Data"
        description="Manage map areas and collections downloaded to this device for offline use."
      />

      <div class="mx-auto max-w-5xl px-4 pb-16 pt-6 sm:px-8 sm:pt-8">
        <!-- Web fallback -->
        <BaseEmptyState
          v-if="!isTauri()"
          :icon="WifiOffIcon"
          title="Native apps only"
          description="Offline data management is available in the Routecamp desktop, iOS, and Android apps."
          size="roomy"
        />

        <BaseCard
          v-else-if="isRefreshingOfflineData"
          radius="xl"
          padding="none"
          :shadow="false"
          class="flex min-h-56 items-center justify-center p-8"
          role="status"
        >
          <div class="text-center">
            <LoaderIcon class="mx-auto h-6 w-6 animate-spin text-primary-700" />
            <p class="mt-3 text-sm font-medium text-gray-700">Refreshing offline data…</p>
            <p class="mt-1 text-xs text-gray-500">
              Waiting for package cleanup and current storage totals.
            </p>
          </div>
        </BaseCard>

        <BaseCard
          v-else-if="refreshError"
          radius="xl"
          padding="none"
          :shadow="false"
          class="p-6 text-center"
          role="alert"
        >
          <p class="text-sm font-semibold text-gray-900">Offline data could not be refreshed</p>
          <p class="mt-1 text-sm text-gray-600">{{ refreshError }}</p>
          <BaseButton class="mt-4" variant="secondary" @click="refreshOfflineDataView">
            Try again
          </BaseButton>
        </BaseCard>

        <template v-else>
          <section aria-labelledby="storage-overview-heading">
            <h2 id="storage-overview-heading" class="sr-only">Offline storage overview</h2>
            <div class="grid gap-3 md:grid-cols-2">
              <BaseCard radius="xl" padding="none" :shadow="false" class="p-4 sm:p-5">
                <div class="flex items-center gap-3">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700"
                  >
                    <DatabaseIcon class="h-5 w-5" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-medium text-gray-500">Offline downloads</p>
                    <div class="mt-0.5 flex flex-wrap items-baseline gap-x-2">
                      <p class="text-lg font-semibold text-gray-900 tabular-nums">
                        {{ formatByteSize(offlineStore.totalSize) }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ entries.length }} package{{ entries.length === 1 ? '' : 's' }}
                      </p>
                    </div>
                  </div>
                </div>
              </BaseCard>

              <BaseCard radius="xl" padding="none" :shadow="false" class="p-4 sm:p-5">
                <div class="flex items-center gap-3">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600"
                  >
                    <MapIcon class="h-5 w-5" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-medium text-gray-500">Map cache</p>
                    <p class="mt-0.5 text-lg font-semibold text-gray-900 tabular-nums">
                      {{ formatByteSize(offlineStore.tileCacheBytes) }}
                    </p>
                  </div>
                  <BaseButton
                    variant="secondary"
                    size="sm"
                    class="min-h-11 !flex-none"
                    :loading="clearingTileCache"
                    :disabled="offlineStore.tileCacheBytes === 0"
                    @click="handleClearTileCache"
                  >
                    <TrashIcon v-if="!clearingTileCache" class="mr-1.5 h-3.5 w-3.5" />
                    {{ clearingTileCache ? 'Clearing' : 'Clear' }}
                  </BaseButton>
                </div>
                <p class="mt-3 text-xs leading-5 text-gray-500">
                  Temporary Routecamp map and terrain tiles saved while browsing. Recently viewed
                  areas may still appear offline, but coverage is incomplete and the device can
                  remove it. Clearing this cache does not remove downloaded collections.
                </p>
              </BaseCard>
            </div>
          </section>

          <section class="mt-8" aria-labelledby="offline-downloads-heading">
            <div class="mb-3 flex items-end justify-between gap-4">
              <div>
                <h2 id="offline-downloads-heading" class="text-base font-semibold text-gray-900">
                  Offline downloads
                </h2>
                <p class="mt-0.5 text-xs text-gray-500">
                  Map areas and collection data kept available without a connection.
                </p>
              </div>
              <BaseButton
                variant="primary"
                size="sm"
                class="min-h-11 shrink-0"
                :disabled="
                  offlineStore.ownerUserId === null ||
                  !connectivityService.isEffectivelyOnline.value ||
                  manualMapDownloadBusy
                "
                :title="
                  offlineStore.ownerUserId === null
                    ? 'Sign in to download map areas'
                    : !connectivityService.isEffectivelyOnline.value
                      ? 'Connect to download map areas'
                      : manualMapDownloadBusy
                        ? 'Finish the current map download first'
                        : undefined
                "
                @click="handleOpenMapAreaSelector"
              >
                <DownloadIcon class="mr-1.5 h-4 w-4" />
                <span class="hidden sm:inline">{{
                  hasManualMapPackage ? 'Manage map areas' : 'Download map areas'
                }}</span>
                <span class="sm:hidden">Map areas</span>
              </BaseButton>
            </div>

            <BaseCard v-if="entries.length === 0" radius="xl" padding="none" :shadow="false">
              <BaseEmptyState
                :icon="WifiOffIcon"
                title="No offline downloads"
                description="Download map areas here, or open a collection and make it available offline."
                size="roomy"
              />
            </BaseCard>

            <div v-else class="space-y-3">
              <!-- Phone and tablet cards -->
              <div class="space-y-3 lg:hidden">
                <BaseCard
                  v-for="entry in entries"
                  :key="entry.workspaceId"
                  radius="xl"
                  padding="sm"
                  :shadow="false"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0 flex-1">
                      <button
                        v-if="entry.packageKind === 'workspace'"
                        class="break-words text-left text-base font-semibold text-emerald-700 hover:cursor-pointer hover:text-emerald-900 hover:underline"
                        @click="handleNavigateToWorkspace(entry.workspaceId)"
                      >
                        {{ workspaceName(entry) }}
                      </button>
                      <p v-else class="break-words text-base font-semibold text-gray-900">
                        {{ workspaceName(entry) }}
                      </p>
                      <div class="mt-1 flex flex-wrap gap-x-2 text-xs text-gray-500">
                        <span v-if="terrainLabel(entry)">{{ terrainLabel(entry) }}</span>
                        <span v-if="entry.photosIncluded">
                          {{ entry.photoCount }} photo{{ entry.photoCount === 1 ? '' : 's' }}
                        </span>
                      </div>
                    </div>
                    <BaseBadge :tone="statusTone(rowStatus(entry))" class="shrink-0">
                      {{ statusLabel(rowStatus(entry)) }}
                    </BaseBadge>
                  </div>

                  <div v-if="entry.activeDownload" class="mt-3 space-y-2">
                    <div class="flex items-center justify-between gap-3 text-xs text-gray-500">
                      <span>{{ entryStatusMessage(entry) }}</span>
                      <span class="tabular-nums">{{ entry.progressPercent }}%</span>
                    </div>
                    <BaseProgressBar :value="entry.progressPercent ?? 0" />
                  </div>

                  <div
                    v-else-if="entryStatusMessage(entry)"
                    :class="[
                      'mt-3 rounded-md border px-3 py-2 text-xs',
                      entryStatusMessageClass(entry),
                    ]"
                  >
                    {{ entryStatusMessage(entry) }}
                  </div>

                  <dl
                    class="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-gray-100 pt-3 text-sm sm:grid-cols-4"
                  >
                    <div>
                      <dt class="text-xs text-gray-500">Size</dt>
                      <dd class="mt-0.5 font-medium text-gray-800 tabular-nums">
                        {{ sizeLabel(entry) }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-xs text-gray-500">Tiles</dt>
                      <dd class="mt-0.5 font-medium text-gray-800 tabular-nums">
                        {{ tileCountLabel(entry) }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-xs text-gray-500">Downloaded</dt>
                      <dd class="mt-0.5 font-medium text-gray-800">
                        {{ downloadedLabel(entry) }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-xs text-gray-500">Style</dt>
                      <dd class="mt-0.5 break-all font-mono text-xs text-gray-600">
                        {{ styleLabel(entry) }}
                      </dd>
                    </div>
                  </dl>

                  <div class="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
                    <BaseButton
                      v-if="canResume(entry)"
                      variant="secondary"
                      size="sm"
                      class="min-h-11"
                      :disabled="!canResumeDownloads"
                      :aria-label="`Resume download for ${workspaceName(entry)}`"
                      @click="handleResume(entry.workspaceId)"
                    >
                      <PlayIcon class="mr-1.5 h-4 w-4" />
                      <span>Resume</span>
                    </BaseButton>
                    <BaseButton
                      v-if="canPause(entry)"
                      variant="secondary"
                      size="sm"
                      class="min-h-11"
                      :aria-label="`Pause download for ${workspaceName(entry)}`"
                      @click="handlePause(entry.workspaceId)"
                    >
                      <PauseIcon class="mr-1.5 h-4 w-4" />
                      <span>Pause</span>
                    </BaseButton>
                    <BaseButton
                      variant="danger"
                      size="sm"
                      class="min-h-11"
                      :loading="offlineStore.deleting[entry.workspaceId]"
                      :aria-label="`Delete offline data for ${workspaceName(entry)}`"
                      @click="handleDelete(entry.workspaceId, workspaceName(entry))"
                    >
                      <TrashIcon
                        v-if="!offlineStore.deleting[entry.workspaceId]"
                        class="mr-1.5 h-4 w-4"
                      />
                      <span>{{
                        offlineStore.deleting[entry.workspaceId] ? 'Deleting' : 'Delete'
                      }}</span>
                    </BaseButton>
                  </div>
                </BaseCard>
              </div>

              <!-- Desktop table -->
              <div
                class="hidden overflow-hidden rounded-xl border border-gray-200 bg-white lg:block"
              >
                <table class="w-full text-sm">
                  <thead>
                    <tr class="border-b border-gray-200 bg-gray-50">
                      <th class="px-4 py-3 text-left font-medium text-gray-700">Download</th>
                      <th class="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                      <th class="px-4 py-3 text-right font-medium text-gray-700">Size</th>
                      <th class="px-4 py-3 text-right font-medium text-gray-700">Tiles</th>
                      <th class="px-4 py-3 text-left font-medium text-gray-700">Downloaded</th>
                      <th class="px-4 py-3 text-left font-medium text-gray-700">Style</th>
                      <th class="px-4 py-3 text-right font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    <tr
                      v-for="entry in entries"
                      :key="entry.workspaceId"
                      class="transition-colors hover:bg-gray-50"
                    >
                      <td class="px-4 py-3">
                        <button
                          v-if="entry.packageKind === 'workspace'"
                          class="text-left font-medium text-emerald-700 hover:cursor-pointer hover:text-emerald-900 hover:underline"
                          @click="handleNavigateToWorkspace(entry.workspaceId)"
                        >
                          {{ workspaceName(entry) }}
                        </button>
                        <span v-else class="font-medium text-gray-900">
                          {{ workspaceName(entry) }}
                        </span>
                        <div v-if="terrainLabel(entry)" class="mt-0.5 text-xs text-gray-400">
                          {{ terrainLabel(entry) }}
                        </div>
                        <div v-if="entry.photosIncluded" class="mt-0.5 text-xs text-gray-400">
                          Includes {{ entry.photoCount }} photos
                        </div>
                      </td>
                      <td class="px-4 py-3">
                        <BaseBadge :tone="statusTone(rowStatus(entry))">
                          {{ statusLabel(rowStatus(entry)) }}
                        </BaseBadge>
                        <div v-if="entry.activeDownload" class="mt-2 min-w-[12rem] space-y-1">
                          <div
                            class="flex items-center justify-between gap-2 text-xs text-gray-500"
                          >
                            <span>{{ entryStatusMessage(entry) }}</span>
                            <span class="tabular-nums">{{ entry.progressPercent }}%</span>
                          </div>
                          <BaseProgressBar :value="entry.progressPercent ?? 0" />
                        </div>
                        <div
                          v-else-if="entryStatusMessage(entry)"
                          :class="['mt-2 text-xs', entryInlineStatusClass(entry)]"
                        >
                          {{ entryStatusMessage(entry) }}
                        </div>
                      </td>
                      <td class="whitespace-nowrap px-4 py-3 text-right text-gray-600 tabular-nums">
                        {{ sizeLabel(entry) }}
                      </td>
                      <td class="whitespace-nowrap px-4 py-3 text-right text-gray-600 tabular-nums">
                        {{ tileCountLabel(entry) }}
                      </td>
                      <td class="whitespace-nowrap px-4 py-3 text-gray-600">
                        {{ downloadedLabel(entry) }}
                      </td>
                      <td class="px-4 py-3 font-mono text-xs text-gray-500">
                        {{ styleLabel(entry) }}
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex items-center justify-end gap-1">
                          <BaseIconButton
                            v-if="canResume(entry)"
                            tone="active"
                            title="Resume download"
                            :disabled="!canResumeDownloads"
                            :aria-label="`Resume download for ${workspaceName(entry)}`"
                            @click="handleResume(entry.workspaceId)"
                          >
                            <PlayIcon class="h-4 w-4" />
                          </BaseIconButton>
                          <BaseIconButton
                            v-if="canPause(entry)"
                            title="Pause download"
                            :aria-label="`Pause download for ${workspaceName(entry)}`"
                            @click="handlePause(entry.workspaceId)"
                          >
                            <PauseIcon class="h-4 w-4" />
                          </BaseIconButton>
                          <BaseIconButton
                            tone="danger"
                            :disabled="offlineStore.deleting[entry.workspaceId]"
                            title="Delete offline data"
                            :aria-label="`Delete offline data for ${workspaceName(entry)}`"
                            @click="handleDelete(entry.workspaceId, workspaceName(entry))"
                          >
                            <LoaderIcon
                              v-if="offlineStore.deleting[entry.workspaceId]"
                              class="h-4 w-4 animate-spin"
                            />
                            <TrashIcon v-else class="h-4 w-4" />
                          </BaseIconButton>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Footer totals -->
                <div
                  class="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600"
                >
                  <span>{{ entries.length }} package{{ entries.length === 1 ? '' : 's' }}</span>
                  <span class="font-medium"
                    >Total: {{ formatByteSize(offlineStore.totalSize) }}</span
                  >
                </div>
              </div>
            </div>
          </section>
        </template>
      </div>
    </main>
  </div>
</template>
